/* New Management Accounts — wizard.
   Captures Section 1 USER PROMPT answers + creates the account doc. */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle } from "@phosphor-icons/react";
import { extractErrorMessage } from "@/lib/apiError";

const C = { bgDark: "#000", panel: "#0A2826", panelHover: "#0D3330", teal: "#0A8B85", tealSoft: "#7BD3CE", orange: "#F59E0B" };

const ClinqMark = () => (
  <span className="relative inline-flex items-baseline font-extrabold tracking-tight text-2xl"
    style={{ fontFamily: "Manrope, sans-serif", letterSpacing: "-0.03em", color: C.teal }}>
    Cl<span className="relative inline-block">ı<span aria-hidden className="absolute left-1/2 -translate-x-1/2 rounded-full clinq-dot-glow"
      style={{ backgroundColor: C.orange, height: "0.5rem", width: "0.5rem", bottom: "1.05em" }}></span></span>nq
  </span>
);

const monthOptions = () => {
  const months = [];
  const now = new Date();
  for (let i = 0; i < 12; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const id = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleString("en-GB", { month: "long", year: "numeric" });
    months.push({ id, label });
  }
  return months;
};

export default function MgmtNew() {
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const months = monthOptions();
  const [form, setForm] = useState({
    company_name: "",
    target_month: months[0].id,
    industry: "retail",
    profile: { sells: "goods", holds_stock: true, first_time: false, mpesa_products: ["paybill"], uses_accrual: false },
  });

  useEffect(() => { api.get("/mgmt/meta").then(({ data }) => setMeta(data)).catch(() => toast.error("Could not load setup")); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setProfile = (k, v) => setForm((f) => ({ ...f, profile: { ...f.profile, [k]: v } }));

  const toggleMpesa = (id) => {
    const cur = form.profile.mpesa_products || [];
    setProfile("mpesa_products", cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  };

  const canNext = () => {
    if (step === 1) return !!form.company_name.trim();
    if (step === 2) return !!form.target_month && !!form.industry;
    return true;
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/mgmt/accounts", form);
      toast.success("Account created");
      navigate(`/mgmt/${data.account_id}`);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Could not create account"));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: C.bgDark, fontFamily: "Manrope, sans-serif" }} data-testid="mgmt-new-page">
      <header style={{ backgroundColor: "#000" }} className="border-b border-white/5">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/dashboard" data-testid="brand-logo-mgmt-new"><ClinqMark /></Link>
          <Link to="/dashboard" className="text-sm text-white/55 hover:text-white inline-flex items-center gap-1.5">
            <ArrowLeft size={14} /> Back to dashboard
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-8 py-12">
        <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.25em] font-semibold mb-3" style={{ color: C.orange }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: C.orange }}></span> Management Accounts · Step {step} of 3
        </div>
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight" style={{ letterSpacing: "-0.03em" }}>
          {step === 1 && <>What business are we <span style={{ color: C.teal }}>reconciling</span>?</>}
          {step === 2 && <>Which month, and what <span style={{ color: C.teal }}>industry</span>?</>}
          {step === 3 && <>A few details about how the business <span style={{ color: C.teal }}>operates</span>.</>}
        </h1>
        <p className="text-white/55 mt-3 max-w-2xl">
          The Clinq Methodology turns your Excel ledger, bank statement and M-Pesa statement into a clean monthly P&amp;L, balance sheet and cashflow.
        </p>

        <div className="mt-10 space-y-6">
          {step === 1 && (
            <Field label="Business name">
              <input type="text" value={form.company_name} onChange={(e) => set("company_name", e.target.value)}
                placeholder="e.g. Amani Foods Limited" data-testid="mgmt-company-name"
                className="w-full px-4 py-3 rounded-md text-sm focus:outline-none focus:border-[#0A8B85]"
                style={{ backgroundColor: "rgba(0,0,0,0.35)", border: "1px solid rgba(255,255,255,0.15)", color: "#fff" }} />
            </Field>
          )}

          {step === 2 && (
            <>
              <Field label="Target month">
                <select value={form.target_month} onChange={(e) => set("target_month", e.target.value)}
                  data-testid="mgmt-target-month"
                  className="w-full px-4 py-3 rounded-md text-sm focus:outline-none"
                  style={{ backgroundColor: "rgba(0,0,0,0.35)", border: "1px solid rgba(255,255,255,0.15)", color: "#fff" }}>
                  {months.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                </select>
              </Field>
              <Field label="Industry">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {(meta?.industries || []).map((ind) => (
                    <button key={ind.id} type="button" onClick={() => set("industry", ind.id)}
                      data-testid={`mgmt-industry-${ind.id}`}
                      className="text-left px-3 py-2.5 rounded-md text-xs font-semibold border transition-all"
                      style={{
                        backgroundColor: form.industry === ind.id ? "rgba(10, 139, 133, 0.18)" : "rgba(0,0,0,0.25)",
                        borderColor: form.industry === ind.id ? C.teal : "rgba(255,255,255,0.12)",
                        color: "#fff",
                      }}>{ind.name}</button>
                  ))}
                </div>
              </Field>
            </>
          )}

          {step === 3 && (
            <>
              <Field label="What does the business sell?">
                <div className="grid grid-cols-3 gap-2">
                  {[{ id: "goods", l: "Goods" }, { id: "services", l: "Services" }, { id: "both", l: "Both" }].map((o) => (
                    <button key={o.id} type="button" onClick={() => setProfile("sells", o.id)}
                      data-testid={`mgmt-sells-${o.id}`}
                      className="px-3 py-2.5 rounded-md text-xs font-semibold border transition-all"
                      style={{
                        backgroundColor: form.profile.sells === o.id ? "rgba(10, 139, 133, 0.18)" : "rgba(0,0,0,0.25)",
                        borderColor: form.profile.sells === o.id ? C.teal : "rgba(255,255,255,0.12)",
                        color: "#fff",
                      }}>{o.l}</button>
                  ))}
                </div>
              </Field>

              <Field label="Does the business hold stock or inventory?">
                <Toggle value={form.profile.holds_stock} onChange={(v) => setProfile("holds_stock", v)} testid="mgmt-holds-stock" />
              </Field>

              <Field label="Which M-Pesa products do you use?">
                <div className="flex flex-wrap gap-2">
                  {[{ id: "till", l: "Till number" }, { id: "paybill", l: "Pay Bill" },
                    { id: "mpesa_business", l: "M-Pesa Business" }, { id: "none", l: "None" }].map((o) => {
                    const on = (form.profile.mpesa_products || []).includes(o.id);
                    return (
                      <button key={o.id} type="button" onClick={() => toggleMpesa(o.id)}
                        data-testid={`mgmt-mpesa-${o.id}`}
                        className="px-3 py-2 rounded-full text-xs font-semibold border transition-all"
                        style={{
                          backgroundColor: on ? "rgba(10, 139, 133, 0.18)" : "rgba(0,0,0,0.25)",
                          borderColor: on ? C.teal : "rgba(255,255,255,0.12)",
                          color: "#fff",
                        }}>{o.l}</button>
                    );
                  })}
                </div>
              </Field>

              <Field label="First time preparing management accounts for this business?">
                <Toggle value={form.profile.first_time} onChange={(v) => setProfile("first_time", v)} testid="mgmt-first-time" />
              </Field>

              <Field label="Use accrual accounting (vs cash basis)?">
                <Toggle value={form.profile.uses_accrual} onChange={(v) => setProfile("uses_accrual", v)} testid="mgmt-accrual" />
              </Field>
            </>
          )}
        </div>

        <div className="mt-12 flex items-center justify-between">
          {step > 1 ? (
            <button onClick={() => setStep((s) => s - 1)} data-testid="mgmt-back-btn"
              className="text-sm font-semibold inline-flex items-center gap-1.5 text-white/65 hover:text-white">
              <ArrowLeft size={14} /> Back
            </button>
          ) : <span />}

          {step < 3 ? (
            <button onClick={() => setStep((s) => s + 1)} disabled={!canNext()} data-testid="mgmt-next-btn"
              className="px-5 py-3 rounded-md font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-50"
              style={{ backgroundColor: C.teal, color: "#fff" }}>
              Next <ArrowRight size={14} />
            </button>
          ) : (
            <button onClick={submit} disabled={busy || !form.company_name.trim()} data-testid="mgmt-create-btn"
              className="px-5 py-3 rounded-md font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-50"
              style={{ backgroundColor: C.teal, color: "#fff" }}>
              <CheckCircle size={14} weight="bold" /> Create & start
            </button>
          )}
        </div>
      </main>

    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.22em] font-bold mb-2" style={{ color: C.tealSoft }}>{label}</label>
      {children}
    </div>
  );
}

function Toggle({ value, onChange, testid }) {
  return (
    <div className="inline-flex rounded-md overflow-hidden border" style={{ borderColor: "rgba(255,255,255,0.12)" }}>
      {[true, false].map((v) => (
        <button key={String(v)} type="button" onClick={() => onChange(v)}
          data-testid={`${testid}-${v ? "yes" : "no"}`}
          className="px-4 py-2 text-xs font-semibold transition-colors"
          style={{
            backgroundColor: value === v ? C.teal : "rgba(0,0,0,0.25)",
            color: value === v ? "#fff" : "rgba(255,255,255,0.6)",
          }}>{v ? "Yes" : "No"}</button>
      ))}
    </div>
  );
}
